import React from 'react';
import { Box, Center, useToken } from '@chakra-ui/react';
import {
  HStack,
  Pressable,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { Button } from 'components/ui/button';
import { ChevronRight, X } from 'lucide-react';
import type { DashboardBlock } from '../../../models/block';
import {
  getBlockDescription,
  getBlockId,
  getBlockName,
  isAssignableBlock,
  normalizeText,
} from './utils';

type Props = {
  activeAssignedBlockNames: Set<string>;
  blockOptions: DashboardBlock[];
  onBackToBlockList: () => void;
  onCreateBlock: (block: DashboardBlock) => void;
  onPatientInstructionsChange: (text: string) => void;
  onSelectBlock: (block: DashboardBlock) => void;
  patientInstructions: string;
  selectedBlock: DashboardBlock | null;
};

// TODO: If block-specific creation flows diverge, split this component into
// smaller per-block creation components and keep this one as the coordinator.
export const BlockCreateContent = ({
  activeAssignedBlockNames,
  blockOptions,
  onBackToBlockList,
  onCreateBlock,
  onPatientInstructionsChange,
  onSelectBlock,
  patientInstructions,
  selectedBlock,
}: Props) => {
  const [secondary200, muted400] = useToken('colors', [
    'secondary.200',
    'muted.400',
  ]);

  return (
    <VStack h={'100%'} justifyContent={'space-between'}>
      {!selectedBlock && (
        <VStack>
          {!blockOptions.length && (
            <Text color={'blueGray.600'}>No block types available.</Text>
          )}

          {blockOptions.map((block) => {
            const blockName = getBlockName(block);
            const blockDescription = getBlockDescription(block);
            const isAlreadyActive = activeAssignedBlockNames.has(
              normalizeText(blockName)
            );
            const isEnabled = isAssignableBlock(block) && !isAlreadyActive;

            return (
              <Pressable
                key={getBlockId(block) || blockName}
                borderBottomWidth={1}
                borderColor={'cloud.200'}
                disabled={!isEnabled}
                onClick={() => {
                  if (!isEnabled) return;
                  onSelectBlock(block);
                }}
                py={3}
                w="100%"
              >
                <HStack alignItems={'center'} justifyContent={'space-between'}>
                  <VStack flex={1} pr={4}>
                    <Text
                      color={isEnabled ? 'secondary.500' : 'muted.400'}
                      fontSize={'lg'}
                      fontWeight={'medium'}
                    >
                      {blockName}
                    </Text>

                    {!!blockDescription && (
                      <Text
                        color={isEnabled ? 'blueGray.600' : 'muted.400'}
                        fontSize={'sm'}
                        mt={0.5}
                      >
                        {blockDescription}
                      </Text>
                    )}
                  </VStack>

                  <Center
                    bgColor={'feather.100'}
                    borderColor={'cloud.200'}
                    borderRadius={'full'}
                    borderWidth={1}
                    h={'40px'}
                    w={'40px'}
                  >
                    <ChevronRight
                      color={isEnabled ? secondary200 : muted400}
                      size={18}
                      strokeWidth={2.5}
                    />
                  </Center>
                </HStack>
              </Pressable>
            );
          })}
        </VStack>
      )}

      {!!selectedBlock && (
        <>
          <VStack>
            <HStack
              alignItems={'flex-start'}
              borderBottomWidth={1}
              borderColor={'cloud.200'}
              justifyContent={'space-between'}
              pb={4}
            >
              <VStack flex={1} pr={3}>
                <Text
                  color={'secondary.500'}
                  fontSize={'xl'}
                  fontWeight={'medium'}
                >
                  {getBlockName(selectedBlock)}
                </Text>

                {!!getBlockDescription(selectedBlock) && (
                  <Text color={'blueGray.600'} fontSize={'sm'} mt={0.5}>
                    {getBlockDescription(selectedBlock)}
                  </Text>
                )}
              </VStack>

              <Pressable
                alignItems={'center'}
                bgColor={'feather.100'}
                borderColor={'cloud.200'}
                borderRadius={'full'}
                borderWidth={1}
                h={'40px'}
                justifyContent={'center'}
                onClick={onBackToBlockList}
                w={'40px'}
              >
                <X color={secondary200} size={18} strokeWidth={2.5} />
              </Pressable>
            </HStack>

            <HStack
              alignItems={'center'}
              justifyContent={'space-between'}
              mt={5}
            >
              <Text
                color={'secondary.500'}
                fontSize={'lg'}
                fontWeight={'medium'}
              >
                Instructions
              </Text>
              <Text color={'blueGray.600'} fontSize={'xs'}>
                {patientInstructions.length}/500
              </Text>
            </HStack>

            <Box
              mt={2}
              borderWidth={1}
              borderColor={'cloud.200'}
              borderRadius={'md'}
              overflow={'hidden'}
            >
              <textarea
                maxLength={500}
                onChange={(e) => onPatientInstructionsChange(e.target.value)}
                placeholder={'Add patient instructions...'}
                style={{
                  border: 0,
                  color: '#2D6670',
                  fontFamily: 'var(--chakra-fonts-body)',
                  fontSize: 'var(--chakra-font-sizes-sm)',
                  height: '120px',
                  outline: 'none',
                  padding: '12px',
                  resize: 'none',
                  width: '100%',
                }}
                value={patientInstructions}
              />
            </Box>
          </VStack>

          <HStack borderTopWidth={1} borderColor={'cloud.200'} pt={4} gap={3}>
            <Button
              backgroundColor={'secondary.500'}
              flex={1}
              maxW={'1/2'}
              onClick={() => {
                onCreateBlock(selectedBlock);
              }}
            >
              <Text color={'white'} fontSize={'md'} fontWeight={'normal'}>
                Assign
              </Text>
            </Button>
          </HStack>
        </>
      )}
    </VStack>
  );
};
